import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, ReferenceLine,
} from 'recharts';
import AMPageShell from './components/AMPageShell';
import { ASSETS } from './data/assetData';


const VINTAGE_COLOURS = {
  2017: '#3b4c4c',
  2018: '#0D9488',
  2019: '#F59E0B',
  2020: '#8B5CF6',
  2021: '#10B981',
  2022: '#EF4444',
  2023: '#64748B',
};

const WATCHLIST_BADGES = {
  'Confirmed': 'bg-green-100 text-green-700',
  'Confirmed with actions': 'bg-blue-100 text-blue-700',
  'Watchlist': 'bg-amber-100 text-amber-700',
  'Escalated': 'bg-red-100 text-red-700',
  'Exit case': 'bg-purple-100 text-purple-700',
};

function getVintageYear(asset) {
  return new Date(asset?.acquisitionDate)?.getFullYear();
}

function getHoldingPeriod(asset) {
  const acq = new Date(asset?.acquisitionDate);
  const now = new Date('2025-12-01');
  return ((now - acq) / (1000 * 60 * 60 * 24 * 365.25))?.toFixed(1);
}

function avg(arr) {
  if (!arr?.length) return 0;
  return arr?.reduce((s, v) => s + v, 0) / arr?.length;
}

export default function VintageAnalysis({ selectedAssets, showToast }) {
  const assets = selectedAssets || ASSETS;
  const navigate = useNavigate();
  const [selectedVintage, setSelectedVintage] = useState('all');
  const [sortKey, setSortKey] = useState('vintageYear');
  const [sortDir, setSortDir] = useState('asc');

  const vintageYears = useMemo(() => {
    const years = [...new Set(assets?.map(getVintageYear))]?.sort();
    return years;
  }, [assets]);

  const filteredAssets = useMemo(() => {
    if (selectedVintage === 'all') return assets;
    return assets?.filter((a) => getVintageYear(a) === Number(selectedVintage));
  }, [assets, selectedVintage]);

  const cohortData = useMemo(() => {
    return vintageYears?.map((yr) => {
      const cohort = assets?.filter((a) => getVintageYear(a) === yr);
      return {
        year: yr,
        assets: cohort,
        avgIRR: avg(cohort?.map((a) => a?.irr ?? 0)),
        avgIRRTarget: avg(cohort?.map((a) => a?.irrTarget ?? 0)),
        avgMOIC: avg(cohort?.map((a) => a?.multiple ?? 0)),
        avgESG: avg(cohort?.map((a) => a?.esgScore ?? 0)),
        totalExposure: cohort?.reduce((s, a) => s + (a?.fundExposure ?? 0), 0),
      };
    });
  }, [assets, vintageYears]);

  // IRR progression chart — normalise to quarters since acquisition
  const irrChartData = useMemo(() => {
    const maxQ = Math.max(...assets?.map((a) => a?.irrHistory?.length ?? 0));
    return Array.from({ length: maxQ }, (_, qi) => {
      const point = { quarter: `Q${qi + 1}` };
      assets?.forEach((a) => {
        const yr = getVintageYear(a);
        if (a?.irrHistory?.[qi]) {
          const key = `${yr}_${a?.name?.split(' ')?.[0]}`;
          point[key] = a?.irrHistory?.[qi]?.irr;
        }
      });
      return point;
    });
  }, [assets]);

  const avgTargetIRR = avg(assets?.map((a) => a?.irrTarget ?? 0));

  // MOIC comparison bar chart
  const moicData = cohortData?.map((c) => ({
    vintage: String(c?.year),
    'IRR Target': c?.avgIRRTarget,
    'IRR Actual': c?.avgIRR,
    'MOIC (scaled)': c?.avgMOIC * 2,
  }));

  // DPI vs TVPI
  const dpiData = [...assets]
    ?.sort((a, b) => (b?.dpi ?? 0) - (a?.dpi ?? 0))
    ?.map((a) => ({
      name: a?.name?.split(' ')?.slice(0, 2)?.join(' '),
      DPI: a?.dpi ?? 0,
      'Unrealised (TVPI-DPI)': Math.max(0, (a?.multiple ?? 1) - (a?.dpi ?? 0)),
      id: a?.id,
    }));

  const sortedAssets = useMemo(() => {
    return [...filteredAssets]?.sort((a, b) => {
      let aVal, bVal;
      switch (sortKey) {
        case 'vintageYear': aVal = getVintageYear(a); bVal = getVintageYear(b); break;
        case 'name': aVal = a?.name; bVal = b?.name; break;
        case 'irr': aVal = a?.irr ?? 0; bVal = b?.irr ?? 0; break;
        case 'irrDelta': aVal = (a?.irr ?? 0) - (a?.irrTarget ?? 0); bVal = (b?.irr ?? 0) - (b?.irrTarget ?? 0); break;
        case 'moic': aVal = a?.multiple ?? 0; bVal = b?.multiple ?? 0; break;
        case 'esgScore': aVal = a?.esgScore ?? 0; bVal = b?.esgScore ?? 0; break;
        case 'riskScore': aVal = a?.riskScore ?? 0; bVal = b?.riskScore ?? 0; break;
        default: aVal = getVintageYear(a); bVal = getVintageYear(b);
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredAssets, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => (
    <span className="ml-1 text-muted-foreground">
      {sortKey === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  return (
    <AMPageShell activeTab="/asset-management/vintage-analysis">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.375rem' }}>Vintage Year Analysis</h2>
          <p className="font-caption text-muted-foreground text-sm mt-0.5">Performance progression by acquisition cohort — Sample data</p>
        </div>

        {/* Vintage Tabs */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedVintage('all')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
              selectedVintage === 'all' ?'bg-primary text-white border-primary' :'bg-background border-border text-muted-foreground hover:border-primary/40'
            }`}
          >
            All Vintages
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              selectedVintage === 'all' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
            }`}>{assets?.length}</span>
          </button>
          {vintageYears?.map((yr) => {
            const count = assets?.filter((a) => getVintageYear(a) === yr)?.length;
            return (
              <button
                key={yr}
                onClick={() => setSelectedVintage(String(yr))}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                  selectedVintage === String(yr)
                    ? 'text-white border-transparent' :'bg-background border-border text-muted-foreground hover:border-primary/40'
                }`}
                style={selectedVintage === String(yr) ? { background: VINTAGE_COLOURS?.[yr] } : {}}
              >
                {yr}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  selectedVintage === String(yr) ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                }`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Cohort Summary Cards */}
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
            {cohortData?.map((c) => (
              <div
                key={c?.year}
                className={`card-interactive bg-card border rounded-lg p-4 w-56 flex-shrink-0 cursor-pointer transition-all ${
                  selectedVintage === String(c?.year) ? 'border-primary shadow-md' : 'border-border'
                }`}
                onClick={() => setSelectedVintage(selectedVintage === String(c?.year) ? 'all' : String(c?.year))}
              >
                <div className="flex items-center justify-between mb-3">
                  <span
                    className="font-heading font-bold text-2xl"
                    style={{ color: VINTAGE_COLOURS?.[c?.year] }}
                  >{c?.year}</span>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{c?.assets?.length} asset{c?.assets?.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {c?.assets?.map((a) => (
                    <span key={a?.id} className="text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                      {a?.name?.split(' ')?.[0]}
                    </span>
                  ))}
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Avg IRR</span>
                    <span className="font-semibold text-foreground">{c?.avgIRR?.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Avg Target</span>
                    <span className="font-semibold text-foreground">{c?.avgIRRTarget?.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Avg MOIC</span>
                    <span className="font-semibold text-foreground">{c?.avgMOIC?.toFixed(2)}×</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Avg ESG</span>
                    <span className="font-semibold text-foreground">{c?.avgESG?.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Exposure</span>
                    <span className="font-semibold text-foreground">£{c?.totalExposure?.toFixed(0)}m</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* IRR Progression Chart */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="font-heading font-semibold text-foreground text-base mb-1">IRR Progression by Vintage</h3>
          <p className="font-caption text-xs text-muted-foreground mb-4">Quarterly IRR normalised to quarters since acquisition — dashed grey line = portfolio avg target IRR</p>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={irrChartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="quarter" tick={{ fontSize: 11, fill: '#64748B' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v, name) => [`${v?.toFixed(1)}%`, name?.replace('_', ' — ')]} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <ReferenceLine y={avgTargetIRR} stroke="#94A3B8" strokeDasharray="6 3" label={{ value: 'Avg Target', position: 'right', fontSize: 10, fill: '#94A3B8' }} />
              {assets?.map((a) => {
                const yr = getVintageYear(a);
                const key = `${yr}_${a?.name?.split(' ')?.[0]}`;
                return (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={VINTAGE_COLOURS?.[yr] || '#64748B'}
                    strokeWidth={1.5}
                    dot={false}
                    connectNulls
                    name={`${yr} — ${a?.name?.split(' ')?.[0]}`}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Performance Comparison Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-heading font-semibold text-foreground text-base">Performance Comparison</h3>
            <p className="font-caption text-xs text-muted-foreground mt-0.5">Click column headers to sort · Click row to view asset detail</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-border">
                  {[
                    { key: 'vintageYear', label: 'Vintage' },
                    { key: 'name', label: 'Asset' },
                    { key: null, label: 'Sector' },
                    { key: null, label: 'Country' },
                    { key: null, label: 'Acq. Date' },
                    { key: null, label: 'Holding (yrs)' },
                    { key: null, label: 'IRR Target' },
                    { key: 'irr', label: 'IRR Actual' },
                    { key: 'irrDelta', label: 'IRR Δ' },
                    { key: 'moic', label: 'MOIC' },
                    { key: null, label: 'DPI' },
                    { key: null, label: 'Cash Yield' },
                    { key: 'esgScore', label: 'ESG' },
                    { key: 'riskScore', label: 'Risk' },
                    { key: null, label: 'Watchlist' },
                  ]?.map((col, i) => (
                    <th
                      key={i}
                      onClick={col?.key ? () => handleSort(col?.key) : undefined}
                      className={`text-left px-3 py-3 font-caption font-semibold text-muted-foreground text-xs uppercase tracking-wide whitespace-nowrap ${
                        col?.key ? 'cursor-pointer hover:text-foreground' : ''
                      }`}
                    >
                      {col?.label}{col?.key && <SortIcon col={col?.key} />}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sortedAssets?.map((asset) => {
                  const irrDelta = (asset?.irr ?? 0) - (asset?.irrTarget ?? 0);
                  const wlC = WATCHLIST_BADGES?.[asset?.watchlistStatus] || 'bg-slate-100 text-slate-600';
                  return (
                    <tr
                      key={asset?.id}
                      onClick={() => navigate(`/asset-management/asset-detail/${asset?.id}`)}
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <td className="px-3 py-3">
                        <span
                          className="font-semibold text-xs px-2 py-0.5 rounded"
                          style={{ background: VINTAGE_COLOURS?.[getVintageYear(asset)] + '20', color: VINTAGE_COLOURS?.[getVintageYear(asset)] }}
                        >
                          {getVintageYear(asset)}
                        </span>
                      </td>
                      <td className="px-3 py-3 font-medium text-foreground whitespace-nowrap">{asset?.name}</td>
                      <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{asset?.sector}</td>
                      <td className="px-3 py-3 text-muted-foreground">{asset?.country}</td>
                      <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{asset?.acquisitionDate?.slice(0, 7)}</td>
                      <td className="px-3 py-3 text-muted-foreground">{getHoldingPeriod(asset)}</td>
                      <td className="px-3 py-3 text-muted-foreground">{asset?.irrTarget?.toFixed(1) ?? '—'}%</td>
                      <td className="px-3 py-3 font-semibold text-foreground">{asset?.irr?.toFixed(1) ?? '—'}%</td>
                      <td className="px-3 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                          irrDelta > 0 ? 'bg-green-50 text-green-700' : irrDelta >= -1 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {irrDelta > 0 ? '+' : ''}{irrDelta?.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-3 py-3 font-semibold text-foreground">{asset?.multiple?.toFixed(2) ?? '—'}×</td>
                      <td className="px-3 py-3 text-muted-foreground">{asset?.dpi?.toFixed(2) ?? '—'}×</td>
                      <td className="px-3 py-3">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          (asset?.cashYieldActual ?? 0) >= (asset?.cashYieldBudget ?? 0)
                            ? 'bg-green-50 text-green-700' :'bg-amber-50 text-amber-700'
                        }`}>
                          {asset?.cashYieldActual?.toFixed(1) ?? '—'}% / {asset?.cashYieldBudget?.toFixed(1) ?? '—'}%
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                          (asset?.esgScore ?? 0) >= 75 ? 'bg-green-50 text-green-700' : (asset?.esgScore ?? 0) >= 60 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {asset?.esgScore ?? '—'}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                          (asset?.riskScore ?? 0) <= 2 ? 'bg-green-50 text-green-700' : (asset?.riskScore ?? 0) === 3 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {asset?.riskScore ?? '—'}/5
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${wlC}`}>
                          {asset?.watchlistStatus}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* MOIC Comparison Bar Chart */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="font-heading font-semibold text-foreground text-base mb-1">IRR vs Target by Vintage</h3>
          <p className="font-caption text-xs text-muted-foreground mb-4">Grouped bars: IRR Target (grey) vs IRR Actual (navy) vs MOIC scaled reference (teal)</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={moicData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="vintage" tick={{ fontSize: 12, fill: '#64748B' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="IRR Target" fill="#94A3B8" radius={[3, 3, 0, 0]} />
              <Bar dataKey="IRR Actual" fill="#3b4c4c" radius={[3, 3, 0, 0]} />
              <Bar dataKey="MOIC (scaled)" fill="#0D9488" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* DPI vs TVPI */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="font-heading font-semibold text-foreground text-base mb-1">DPI vs TVPI Analysis</h3>
          <p className="font-caption text-xs text-muted-foreground mb-1">Stacked bars: DPI (realised, teal) + Unrealised value (navy) — sorted by DPI descending</p>
          <p className="font-caption text-xs text-amber-600 mb-4">ℹ Nordic Fibre Network shows highest DPI at 0.89× — exit case under consideration</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dpiData} margin={{ top: 5, right: 20, bottom: 60, left: 0 }} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={(v) => `${v?.toFixed(1)}×`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748B' }} width={110} />
              <Tooltip formatter={(v, name) => [`${v?.toFixed(2)}×`, name]} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="DPI" stackId="a" fill="#0D9488" name="DPI (Realised)" />
              <Bar dataKey="Unrealised (TVPI-DPI)" stackId="a" fill="#3b4c4c" name="Unrealised Value" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </AMPageShell>
  );
}
